// src/data/mock/activities.ts

/**
 * Activities, Events, and Trips Data Structure
 * 
 * Complete information about Champions Academy activities,
 * camps, local/international trips, and events
 */

export type ActivityType = 'camp' | 'local_trip' | 'international_trip' | 'event' | 'tournament' | 'workshop';
export type ActivityCategory = 'football' | 'basketball' | 'swimming' | 'karate' | 'tennis' | 'multi-sport' | 'community';
export type ActivityStatus = 'completed' | 'upcoming' | 'ongoing' | 'cancelled';

export interface Activity {
  id: string;
  slug: string;
  title: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  fullDescription?: {
    en: string;
    ar: string;
  };
  image: string;
  gallery?: string[];
  date: string;
  endDate?: string;
  type: ActivityType;
  category: ActivityCategory;
  location: {
    en: string;
    ar: string;
  };
  
  // Participants
  participants?: number;
  ageGroup?: {
    en: string;
    ar: string;
  };
  
  // Details
  highlights?: {
    en: string[];
    ar: string[];
  };
  objectives?: {
    en: string[];
    ar: string[];
  };
  achievements?: {
    en: string[];
    ar: string[];
  };
  
  // International trips specific
  country?: string;
  duration?: number; // days
  partner?: string;
  
  // Status
  status?: ActivityStatus;
  featured?: boolean;
  
  // Media
  videoUrl?: string;
  tags?: string[];
  
  // Organizers
  organizers?: string[];
  coaches?: string[];
}

/**
 * Activities Database
 */
export const activities: Activity[] = [
  // ===== CAMPS =====
  {
    id: 'camp-001',
    slug: 'summer-football-camp-2023',
    title: {
      en: 'Summer Football Camp 2023',
      ar: 'معسكر كرة القدم الصيفي 2023'
    },
    description: {
      en: 'Intensive football training camp with Real Madrid Foundation coaching methodology. Over 500 young players participated in this world-class program.',
      ar: 'معسكر تدريب كرة قدم مكثف بمنهجية تدريب مؤسسة ريال مدريد. شارك أكثر من 500 لاعب شاب في هذا البرنامج العالمي.'
    },
    fullDescription: {
      en: 'Our flagship summer football camp brought together over 500 young athletes for an intensive 6-week training program. Using Real Madrid Foundation\'s proven coaching methodology, participants received world-class technical training, tactical education, and character development. The camp featured daily training sessions, competitive matches, fitness assessments, and mentorship from professional coaches.',
      ar: 'جمع معسكر كرة القدم الصيفي الرئيسي لدينا أكثر من 500 رياضي شاب لبرنامج تدريبي مكثف لمدة 6 أسابيع. باستخدام منهجية التدريب المثبتة لمؤسسة ريال مدريد، تلقى المشاركون تدريباً فنياً عالمياً وتعليماً تكتيكياً وتطوير الشخصية. تضمن المعسكر جلسات تدريب يومية ومباريات تنافسية وتقييمات لياقة وتوجيه من مدربين محترفين.'
    },
    image: '/assets/activities/summer-camp-2023.jpg',
    gallery: [
      '/assets/activities/camp-1.jpg',
      '/assets/activities/camp-2.jpg',
      '/assets/activities/camp-3.jpg',
      '/assets/activities/camp-4.jpg'
    ],
    date: '2023-07-01',
    endDate: '2023-08-15',
    type: 'camp',
    category: 'football',
    location: { 
      en: 'Main Branch, Gaza City', 
      ar: 'الفرع الرئيسي، مدينة غزة' 
    },
    participants: 500,
    ageGroup: {
      en: '8-16 years',
      ar: '8-16 سنة'
    },
    duration: 45,
    highlights: {
      en: [
        'Real Madrid Foundation methodology',
        '500+ young participants',
        '6 weeks intensive training',
        'Professional coaching staff',
        'Daily competitive matches',
        'Character development program',
        'Fitness and nutrition workshops',
        'Certificates and awards ceremony'
      ],
      ar: [
        'منهجية مؤسسة ريال مدريد',
        '500+ مشارك شاب',
        '6 أسابيع تدريب مكثف',
        'طاقم تدريب محترف',
        'مباريات تنافسية يومية',
        'برنامج تطوير الشخصية',
        'ورش عمل اللياقة والتغذية',
        'حفل شهادات وجوائز'
      ]
    },
    objectives: {
      en: [
        'Develop technical football skills',
        'Improve tactical understanding',
        'Build physical fitness and conditioning',
        'Foster teamwork and leadership',
        'Instill discipline and sportsmanship'
      ],
      ar: [
        'تطوير المهارات الفنية لكرة القدم',
        'تحسين الفهم التكتيكي',
        'بناء اللياقة البدنية والتكييف',
        'تعزيز العمل الجماعي والقيادة',
        'غرس الانضباط والروح الرياضية'
      ]
    },
    achievements: {
      en: [
        '95% participant satisfaction rate',
        '50+ players selected for advanced program',
        '3 players scouted for professional academies',
        'Community impact award winner'
      ],
      ar: [
        '95٪ معدل رضا المشاركين',
        '50+ لاعب تم اختيارهم للبرنامج المتقدم',
        '3 لاعبين تم اكتشافهم لأكاديميات محترفة',
        'فائز بجائزة التأثير المجتمعي'
      ]
    },
    status: 'completed',
    featured: true,
    partner: 'Real Madrid Foundation',
    coaches: ['Coach Tariq Hamdan', 'Coach Yasser Abu Daqqa'],
    tags: ['football', 'summer', 'youth', 'training', 'real-madrid']
  },

  {
    id: 'camp-002',
    slug: 'basketball-winter-camp-2023',
    title: {
      en: 'Basketball Winter Camp 2023',
      ar: 'معسكر كرة السلة الشتوي 2023'
    },
    description: {
      en: 'Intensive basketball training camp focusing on fundamental skills, team play, and competitive spirit with professional coaching.',
      ar: 'معسكر تدريب كرة سلة مكثف يركز على المهارات الأساسية واللعب الجماعي والروح التنافسية مع تدريب محترف.'
    },
    image: '/assets/activities/basketball-camp-2023.jpg',
    date: '2023-12-15',
    endDate: '2024-01-15',
    type: 'camp',
    category: 'basketball',
    location: { 
      en: 'Main Branch Sports Hall', 
      ar: 'صالة الفرع الرئيسي الرياضية' 
    },
    participants: 200,
    ageGroup: {
      en: '10-18 years',
      ar: '10-18 سنة'
    },
    duration: 30,
    status: 'completed',
    coaches: ['Coach Nidal Qeshta'],
    tags: ['basketball', 'winter', 'training']
  },

  {
    id: 'camp-003',
    slug: 'swimming-summer-intensive-2024',
    title: {
      en: 'Swimming Summer Intensive 2024',
      ar: 'معسكر السباحة الصيفي المكثف 2024'
    },
    description: {
      en: 'Comprehensive swimming program covering all strokes, water safety, and competitive techniques with Olympic-standard training.',
      ar: 'برنامج سباحة شامل يغطي جميع السباحات والسلامة المائية والتقنيات التنافسية مع تدريب بمعايير أولمبية.'
    },
    image: '/assets/activities/swimming-camp-2024.jpg',
    date: '2024-07-01',
    endDate: '2024-08-01',
    type: 'camp',
    category: 'swimming',
    location: { 
      en: 'Olympic Swimming Pool, Main Branch', 
      ar: 'المسبح الأولمبي، الفرع الرئيسي' 
    },
    participants: 150,
    ageGroup: {
      en: '6-16 years',
      ar: '6-16 سنة'
    },
    duration: 30,
    status: 'upcoming',
    featured: true,
    coaches: ['Coach Laila Al-Bayoumi'],
    tags: ['swimming', 'summer', 'olympic']
  },

  // ===== LOCAL TRIPS =====
  {
    id: 'trip-local-001',
    slug: 'national-swimming-championship-2023',
    title: {
      en: 'National Swimming Championship 2023',
      ar: 'بطولة السباحة الوطنية 2023'
    },
    description: {
      en: 'Our swimming team competed at the national level, bringing home multiple gold medals and setting new Palestinian records.',
      ar: 'تنافس فريق السباحة لدينا على المستوى الوطني، وحصل على العديد من الميداليات الذهبية وسجل أرقاماً قياسية فلسطينية جديدة.'
    },
    fullDescription: {
      en: 'Champions Academy swimming team made history at the National Swimming Championship, dominating the competition with 12 gold medals, 8 silver medals, and 5 bronze medals. Our athletes set 3 new national records and demonstrated exceptional skill and determination. This achievement marked our team as the strongest swimming delegation in Palestinian sports history.',
      ar: 'صنع فريق السباحة لأكاديمية تشامبيونز التاريخ في بطولة السباحة الوطنية، حيث هيمن على المنافسة بـ 12 ميدالية ذهبية و8 ميداليات فضية و5 ميداليات برونزية. سجل رياضيونا 3 أرقام قياسية وطنية جديدة وأظهروا مهارة وتصميماً استثنائيين. سجل هذا الإنجاز فريقنا كأقوى وفد سباحة في تاريخ الرياضة الفلسطينية.'
    },
    image: '/assets/activities/swimming-championship-2023.jpg',
    gallery: [
      '/assets/activities/swim-1.jpg',
      '/assets/activities/swim-2.jpg',
      '/assets/activities/swim-3.jpg'
    ],
    date: '2023-05-15',
    endDate: '2023-05-18',
    type: 'local_trip',
    category: 'swimming',
    location: { 
      en: 'Ramallah Sports Complex, West Bank', 
      ar: 'مجمع رام الله الرياضي، الضفة الغربية' 
    },
    participants: 30,
    duration: 4,
    achievements: {
      en: [
        '12 Gold Medals',
        '8 Silver Medals',
        '5 Bronze Medals',
        '3 New National Records',
        'Best Team Performance Award',
        'Coach of the Championship Award'
      ],
      ar: [
        '12 ميدالية ذهبية',
        '8 ميداليات فضية',
        '5 ميداليات برونزية',
        '3 أرقام قياسية وطنية جديدة',
        'جائزة أفضل أداء جماعي',
        'جائزة مدرب البطولة'
      ]
    },
    status: 'completed',
    featured: true,
    coaches: ['Coach Laila Al-Bayoumi'],
    tags: ['swimming', 'championship', 'national', 'medals']
  },

  {
    id: 'trip-local-002',
    slug: 'west-bank-football-tour-2023',
    title: {
      en: 'West Bank Football Tour 2023',
      ar: 'جولة كرة القدم في الضفة الغربية 2023'
    },
    description: {
      en: 'Educational and competitive football tour across West Bank cities, playing friendly matches and visiting historical sites.',
      ar: 'جولة كرة قدم تعليمية وتنافسية عبر مدن الضفة الغربية، مع لعب مباريات ودية وزيارة المواقع التاريخية.'
    },
    image: '/assets/activities/westbank-tour-2023.jpg',
    date: '2023-04-10',
    endDate: '2023-04-15',
    type: 'local_trip',
    category: 'football',
    location: { 
      en: 'Ramallah, Nablus, Hebron', 
      ar: 'رام الله، نابلس، الخليل' 
    },
    participants: 45,
    duration: 6,
    highlights: {
      en: [
        '5 friendly matches played',
        'Cultural exchange with local teams',
        'Historical site visits',
        'Unity and solidarity events',
        'Team building activities'
      ],
      ar: [
        '5 مباريات ودية',
        'تبادل ثقافي مع الفرق المحلية',
        'زيارات للمواقع التاريخية',
        'فعاليات وحدة وتضامن',
        'أنشطة بناء الفريق'
      ]
    },
    status: 'completed',
    tags: ['football', 'tour', 'westbank', 'cultural']
  },

  // ===== INTERNATIONAL TRIPS =====
  {
    id: 'trip-int-001',
    slug: 'barcelona-training-experience-2022',
    title: {
      en: 'Barcelona Training Experience 2022',
      ar: 'تجربة تدريب برشلونة 2022'
    },
    description: {
      en: 'Elite players traveled to Barcelona to train at FC Barcelona\'s legendary La Masia facilities and learn from world-class coaches.',
      ar: 'سافر لاعبون نخبة إلى برشلونة للتدريب في مرافق لا ماسيا الأسطورية لنادي برشلونة والتعلم من مدربين عالميين.'
    },
    fullDescription: {
      en: 'Twenty-five of our most talented young players were selected for an unforgettable experience at FC Barcelona\'s renowned training facilities. The week-long program included daily training sessions using La Masia\'s famous methodology, tactical workshops with Barcelona coaches, friendly matches against youth teams, and tours of Camp Nou stadium. This life-changing experience opened doors for several players who were later invited to Barcelona\'s talent scouting programs.',
      ar: 'تم اختيار خمسة وعشرين من أفضل لاعبينا الشباب الموهوبين لتجربة لا تُنسى في مرافق التدريب الشهيرة لنادي برشلونة. تضمن البرنامج الذي استمر أسبوعاً جلسات تدريب يومية باستخدام منهجية لا ماسيا الشهيرة، وورش عمل تكتيكية مع مدربي برشلونة، ومباريات ودية ضد فرق الشباب، وجولات في ملعب كامب نو. فتحت هذه التجربة التي غيرت الحياة أبواباً للعديد من اللاعبين الذين تمت دعوتهم لاحقاً لبرامج اكتشاف المواهب في برشلونة.'
    },
    image: '/assets/activities/barcelona-2022.jpg',
    gallery: [
      '/assets/activities/barca-1.jpg',
      '/assets/activities/barca-2.jpg',
      '/assets/activities/barca-3.jpg',
      '/assets/activities/barca-4.jpg',
      '/assets/activities/barca-5.jpg'
    ],
    date: '2022-08-10',
    endDate: '2022-08-17',
    type: 'international_trip',
    category: 'football',
    location: { 
      en: 'Barcelona, Spain', 
      ar: 'برشلونة، إسبانيا' 
    },
    country: 'Spain',
    participants: 25,
    duration: 8,
    ageGroup: {
      en: '14-17 years',
      ar: '14-17 سنة'
    },
    highlights: {
      en: [
        'Training at La Masia facilities',
        'Sessions with Barcelona coaches',
        'Friendly matches vs Barcelona youth teams',
        'Camp Nou stadium tour',
        'Meeting with professional players',
        'Cultural exchange activities',
        'Spanish language classes',
        'City tours and team bonding'
      ],
      ar: [
        'التدريب في مرافق لا ماسيا',
        'جلسات مع مدربي برشلونة',
        'مباريات ودية ضد فرق شباب برشلونة',
        'جولة في ملعب كامب نو',
        'لقاء مع لاعبين محترفين',
        'أنشطة تبادل ثقافي',
        'دروس اللغة الإسبانية',
        'جولات في المدينة وتواصل الفريق'
      ]
    },
    objectives: {
      en: [
        'Experience world-class training',
        'Learn La Masia methodology',
        'Cultural exchange',
        'Talent exposure',
        'Build international connections'
      ],
      ar: [
        'تجربة تدريب عالمي',
        'تعلم منهجية لا ماسيا',
        'التبادل الثقافي',
        'عرض المواهب',
        'بناء اتصالات دولية'
      ]
    },
    achievements: {
      en: [
        '3 players invited to Barcelona scouting program',
        'Certificate of completion from FC Barcelona',
        'Lifetime memories and friendships',
        'Enhanced technical skills',
        'International experience gained'
      ],
      ar: [
        '3 لاعبين تمت دعوتهم لبرنامج الكشافة في برشلونة',
        'شهادة إتمام من نادي برشلونة',
        'ذكريات وصداقات مدى الحياة',
        'تعزيز المهارات الفنية',
        'اكتساب خبرة دولية'
      ]
    },
    status: 'completed',
    featured: true,
    partner: 'FC Barcelona',
    coaches: ['Coach Tariq Hamdan'],
    tags: ['football', 'barcelona', 'international', 'la-masia', 'spain']
  },

  {
    id: 'trip-int-002',
    slug: 'real-madrid-youth-exchange-2023',
    title: {
      en: 'Real Madrid Youth Exchange Program 2023',
      ar: 'برنامج تبادل شباب ريال مدريد 2023'
    },
    description: {
      en: 'Incredible opportunity for young players to train at Real Madrid\'s facilities, participate in friendly matches, and experience Spanish football culture.',
      ar: 'فرصة رائعة للاعبين الشباب للتدريب في مرافق ريال مدريد والمشاركة في مباريات ودية وتجربة ثقافة كرة القدم الإسبانية.'
    },
    fullDescription: {
      en: 'Through our partnership with Real Madrid Foundation, 20 talented players experienced a transformative 10-day program in Madrid. Training at the world-famous Ciudad Real Madrid (Valdebebas), our athletes learned from UEFA-certified coaches, participated in international youth tournaments, and witnessed first-hand how one of the world\'s greatest clubs operates. The program included exclusive stadium tours, meetings with Real Madrid legends, and cultural immersion in Spanish life.',
      ar: 'من خلال شراكتنا مع مؤسسة ريال مدريد، اختبر 20 لاعباً موهوباً برنامجاً تحويلياً لمدة 10 أيام في مدريد. في التدريب في سيوداد ريال مدريد الشهيرة عالمياً (فالديبيباس)، تعلم رياضيونا من مدربين معتمدين من UEFA، وشاركوا في بطولات شبابية دولية، وشهدوا بشكل مباشر كيفية عمل أحد أعظم الأندية في العالم. تضمن البرنامج جولات حصرية في الملعب ولقاءات مع أساطير ريال مدريد وانغماس ثقافي في الحياة الإسبانية.'
    },
    image: '/assets/activities/real-madrid-2023.jpg',
    gallery: [
      '/assets/activities/madrid-1.jpg',
      '/assets/activities/madrid-2.jpg',
      '/assets/activities/madrid-3.jpg',
      '/assets/activities/madrid-4.jpg'
    ],
    date: '2023-04-20',
    endDate: '2023-04-30',
    type: 'international_trip',
    category: 'football',
    location: { 
      en: 'Madrid, Spain', 
      ar: 'مدريد، إسبانيا' 
    },
    country: 'Spain',
    participants: 20,
    duration: 10,
    ageGroup: {
      en: '13-16 years',
      ar: '13-16 سنة'
    },
    highlights: {
      en: [
        'Training at Ciudad Real Madrid (Valdebebas)',
        'Coached by Real Madrid Foundation staff',
        'Friendly matches against Spanish teams',
        'Santiago Bernabéu Stadium VIP tour',
        'Meet & greet with Real Madrid players',
        'Participation in youth tournament',
        'Spanish language and culture classes',
        'Professional photo and video documentation'
      ],
      ar: [
        'التدريب في سيوداد ريال مدريد (فالديبيباس)',
        'تدريب من قبل طاقم مؤسسة ريال مدريد',
        'مباريات ودية ضد فرق إسبانية',
        'جولة VIP في ملعب سانتياغو برنابيو',
        'لقاء وترحيب مع لاعبي ريال مدريد',
        'المشاركة في بطولة الشباب',
        'دروس اللغة والثقافة الإسبانية',
        'توثيق احترافي بالصور والفيديو'
      ]
    },
    achievements: {
      en: [
        'Winner of youth tournament bronze medal',
        '2 players received scholarship offers',
        'Real Madrid Foundation certificate',
        'Featured in Spanish sports media',
        'Established lasting international connections'
      ],
      ar: [
        'الفوز بالميدالية البرونزية في بطولة الشباب',
        'حصل لاعبان على عروض منح دراسية',
        'شهادة من مؤسسة ريال مدريد',
        'ظهور في وسائل الإعلام الرياضية الإسبانية',
        'إنشاء علاقات دولية دائمة'
      ]
    },
    status: 'completed',
    featured: true,
    partner: 'Real Madrid Foundation',
    coaches: ['Coach Tariq Hamdan', 'Coach Yasser Abu Daqqa'],
    videoUrl: 'https://youtube.com/watch?v=example',
    tags: ['football', 'real-madrid', 'international', 'spain', 'exchange']
  },

  {
    id: 'trip-int-003',
    slug: 'jordan-basketball-tournament-2023',
    title: {
      en: 'Jordan International Basketball Tournament 2023',
      ar: 'بطولة الأردن الدولية لكرة السلة 2023'
    },
    description: {
      en: 'Championship performance at Jordan International Youth Basketball Tournament with teams from across the Middle East.',
      ar: 'أداء بطولي في بطولة الأردن الدولية للشباب لكرة السلة مع فرق من جميع أنحاء الشرق الأوسط.'
    },
    image: '/assets/activities/jordan-basketball-2023.jpg',
    date: '2023-10-05',
    endDate: '2023-10-12',
    type: 'international_trip',
    category: 'basketball',
    location: { 
      en: 'Amman, Jordan', 
      ar: 'عمان، الأردن' 
    },
    country: 'Jordan',
    participants: 15,
    duration: 8,
    achievements: {
      en: [
        'Silver Medal - Tournament Runner-up',
        'Best Defensive Team Award',
        '2 Players in All-Tournament Team',
        'Fair Play Award'
      ],
      ar: [
        'الميدالية الفضية - الوصيف',
        'جائزة أفضل دفاع جماعي',
        'لاعبان في فريق البطولة المثالي',
        'جائزة اللعب النظيف'
      ]
    },
    status: 'completed',
    featured: true,
    coaches: ['Coach Nidal Qeshta'],
    tags: ['basketball', 'jordan', 'tournament', 'international']
  },

  // ===== EVENTS =====
  {
    id: 'event-001',
    slug: 'ramadan-football-tournament-2023',
    title: {
      en: 'Ramadan Football Tournament 2023',
      ar: 'دوري رمضان لكرة القدم 2023'
    },
    description: {
      en: 'Annual community football tournament during Ramadan, bringing together players and families for evening matches and breaking fast together.',
      ar: 'دوري كرة قدم مجتمعي سنوي خلال شهر رمضان، يجمع اللاعبين والعائلات لمباريات مسائية وإفطار جماعي.'
    },
    fullDescription: {
      en: 'Our beloved annual tradition, the Ramadan Football Tournament, brings the entire community together during the blessed month. Over 1000 participants across all age groups compete in evening matches, creating an atmosphere of unity, sportsmanship, and spirituality. Families gather to watch, share meals at sunset, and celebrate together. This tournament embodies our values of community, faith, and athletic excellence.',
      ar: 'تقليدنا السنوي المحبوب، دوري رمضان لكرة القدم، يجمع المجتمع بأكمله خلال الشهر المبارك. يتنافس أكثر من 1000 مشارك عبر جميع الفئات العمرية في مباريات مسائية، مما يخلق جواً من الوحدة والروح الرياضية والروحانية. تجتمع العائلات للمشاهدة ومشاركة الوجبات عند غروب الشمس والاحتفال معاً. تجسد هذه البطولة قيمنا المتمثلة في المجتمع والإيمان والتميز الرياضي.'
    },
    image: '/assets/activities/ramadan-tournament-2023.jpg',
    gallery: [
      '/assets/activities/ramadan-1.jpg',
      '/assets/activities/ramadan-2.jpg',
      '/assets/activities/ramadan-3.jpg',
      '/assets/activities/ramadan-4.jpg'
    ],
    date: '2023-03-25',
    endDate: '2023-04-20',
    type: 'event',
    category: 'football',
    location: { 
      en: 'All Branches', 
      ar: 'جميع الفروع' 
    },
    participants: 1000,
    duration: 27,
    ageGroup: {
      en: 'All ages',
      ar: 'جميع الأعمار'
    },
    highlights: {
      en: [
        '32 teams competing',
        'Evening matches after Iftar',
        'Community Iftar gatherings',
        'Prize ceremony with trophies',
        'Family-friendly atmosphere',
        'Special Ramadan activities',
        'Food and refreshments provided',
        'Photography and memories'
      ],
      ar: [
        '32 فريقاً متنافساً',
        'مباريات مسائية بعد الإفطار',
        'إفطارات جماعية',
        'حفل جوائز مع كؤوس',
        'جو عائلي',
        'أنشطة رمضانية خاصة',
        'طعام ومرطبات',
        'تصوير وذكريات'
      ]
    },
    objectives: {
      en: [
        'Foster community spirit',
        'Promote healthy Ramadan activities',
        'Encourage family participation',
        'Celebrate faith and sports',
        'Build lasting memories'
      ],
      ar: [
        'تعزيز روح المجتمع',
        'تشجيع الأنشطة الصحية في رمضان',
        'تشجيع المشاركة العائلية',
        'الاحتفال بالإيمان والرياضة',
        'بناء ذكريات دائمة'
      ]
    },
    status: 'completed',
    featured: true,
    tags: ['ramadan', 'tournament', 'community', 'football', 'family']
  },

  {
    id: 'event-002',
    slug: 'regional-karate-championship-2023',
    title: {
      en: 'Regional Karate Championship 2023',
      ar: 'بطولة الكاراتيه الإقليمية 2023'
    },
    description: {
      en: 'Our karate students demonstrated exceptional skill and discipline, winning multiple medals at the regional championship.',
      ar: 'أظهر طلاب الكاراتيه لدينا مهارة وانضباطاً استثنائيين، وفازوا بالعديد من الميداليات في البطولة الإقليمية.'
    },
    image: '/assets/activities/karate-championship-2023.jpg',
    date: '2023-06-10',
    endDate: '2023-06-11',
    type: 'event',
    category: 'karate',
    location: { 
      en: 'Gaza Sports Hall', 
      ar: 'صالة غزة الرياضية' 
    },
    participants: 150,
    duration: 2,
    achievements: {
      en: [
        '8 Gold Medals',
        '12 Silver Medals',
        '10 Bronze Medals',
        '5 Students advanced to Black Belt testing',
        'Best Dojo Award'
      ],
      ar: [
        '8 ميداليات ذهبية',
        '12 ميدالية فضية',
        '10 ميداليات برونزية',
        '5 طلاب تقدموا لاختبار الحزام الأسود',
        'جائزة أفضل دوجو'
      ]
    },
    status: 'completed',
    featured: true,
    coaches: ['Sensei Rami Al-Ghoul'],
    tags: ['karate', 'championship', 'martial-arts', 'medals']
  },

  {
    id: 'event-003',
    slug: 'annual-sports-day-2024',
    title: {
      en: 'Annual Sports Day 2024',
      ar: 'اليوم الرياضي السنوي 2024'
    },
    description: {
      en: 'Celebration of sports with activities across all disciplines, family entertainment, and community engagement.',
      ar: 'احتفال بالرياضة مع أنشطة عبر جميع التخصصات، وترفيه عائلي، ومشاركة مجتمعية.'
    },
    image: '/assets/activities/sports-day-2024.jpg',
    date: '2024-05-01',
    type: 'event',
    category: 'multi-sport',
    location: { 
      en: 'Main Branch Campus', 
      ar: 'حرم الفرع الرئيسي' 
    },
    participants: 2000,
    duration: 1,
    highlights: {
      en: [
        'Multi-sport demonstrations',
        'Try different sports stations',
        'Family competitions',
        'Food festival',
        'Live entertainment',
        'Awards ceremony',
        'Face painting and games',
        'Photo booth'
      ],
      ar: [
        'عروض رياضية متعددة',
        'محطات تجربة رياضات مختلفة',
        'مسابقات عائلية',
        'مهرجان طعام',
        'ترفيه حي',
        'حفل جوائز',
        'رسم وجوه وألعاب',
        'كشك صور'
      ]
    },
    status: 'upcoming',
    featured: true,
    tags: ['multi-sport', 'community', 'family', 'festival']
  },

  {
    id: 'event-004',
    slug: 'international-coaches-workshop-2024',
    title: {
      en: 'International Coaches Workshop 2024',
      ar: 'ورشة عمل المدربين الدولية 2024'
    },
    description: {
      en: 'Professional development workshop with international coaches covering modern training methods and sports science.',
      ar: 'ورشة تطوير مهني مع مدربين دوليين تغطي طرق التدريب الحديثة وعلوم الرياضة.'
    },
    image: '/assets/activities/coaches-workshop-2024.jpg',
    date: '2024-03-15',
    endDate: '2024-03-17',
    type: 'workshop',
    category: 'multi-sport',
    location: { 
      en: 'Main Branch Conference Hall', 
      ar: 'قاعة المؤتمرات بالفرع الرئيسي' 
    },
    participants: 80,
    duration: 3,
    status: 'completed',
    tags: ['workshop', 'coaching', 'professional-development']
  },

  {
    id: 'tournament-001',
    slug: 'champions-cup-2023',
    title: {
      en: 'Champions Cup 2023',
      ar: 'كأس تشامبيونز 2023'
    },
    description: {
      en: 'Premier annual football tournament featuring teams from all our branches competing for the prestigious Champions Cup.',
      ar: 'بطولة كرة القدم السنوية الرئيسية التي تضم فرقاً من جميع فروعنا تتنافس على كأس تشامبيونز المرموق.'
    },
    image: '/assets/activities/champions-cup-2023.jpg',
    date: '2023-11-10',
    endDate: '2023-11-25',
    type: 'tournament',
    category: 'football',
    location: { 
      en: 'All Branches', 
      ar: 'جميع الفروع' 
    },
    participants: 500,
    duration: 16,
    status: 'completed',
    featured: true,
    tags: ['tournament', 'football', 'championship']
  }
];

/**
 * Filtered Activities by Type
 */
export const camps = activities.filter(a => a.type === 'camp');
export const localTrips = activities.filter(a => a.type === 'local_trip');
export const internationalTrips = activities.filter(a => a.type === 'international_trip');
export const events = activities.filter(a => a.type === 'event');
export const tournaments = activities.filter(a => a.type === 'tournament');
export const workshops = activities.filter(a => a.type === 'workshop');

/**
 * Activity Statistics
 */
export const activityStats = {
  total: activities.length,
  camps: camps.length,
  localTrips: localTrips.length,
  internationalTrips: internationalTrips.length,
  events: events.length,
  totalParticipants: activities.reduce((sum, a) => sum + (a.participants || 0), 0),
  countries: [...new Set(activities.map(a => a.country).filter(Boolean))].length,
  featured: activities.filter(a => a.featured).length,
  upcoming: activities.filter(a => a.status === 'upcoming').length,
  completed: activities.filter(a => a.status === 'completed').length
};

/**
 * Helper Functions
 */
export const getFeaturedActivities = () => {
  return activities.filter(a => a.featured === true);
};

export const getActivitiesByType = (type: ActivityType) => {
  return activities.filter(a => a.type === type);
};

export const getActivitiesByCategory = (category: ActivityCategory) => {
  return activities.filter(a => a.category === category);
};

export const getActivitiesByStatus = (status: ActivityStatus) => {
  return activities.filter(a => a.status === status);
};

export const getUpcomingActivities = () => {
  return activities.filter(a => a.status === 'upcoming');
};

export const getActivityBySlug = (slug: string) => {
  return activities.find(a => a.slug === slug);
};

// Export default
export default {
  activities,
  camps,
  localTrips,
  internationalTrips,
  events,
  tournaments,
  workshops,
  activityStats,
  getFeaturedActivities,
  getActivitiesByType,
  getActivitiesByCategory,
  getActivitiesByStatus,
  getUpcomingActivities,
  getActivityBySlug
};
